// src/pages/loans/CreateLoan.tsx
import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import { getProfiles } from "../../services/profileService";
import AutocompleteCustomer from "../../components/AutocompleteCustomer";
import InputField from "../../components/InputField";
import Button from "../../components/Button";
import { useNavigate } from "react-router-dom";
import { createLoan } from "../../services/loanService";
import axiosClient from "../../api/axiosClient";
import toast from "react-hot-toast";
// import { useAuth } from "../../context/AuthContext";
import Modal from "../../components/Modal";

type LoanPayload = {
  customerId?: string;
  vehicleId?: string;
  principal?: string;
  interestRate?: string;
  tenure?: string;
  startDate?: string;
  loanType?: string;
  documents?: File[];
  customerMobile?: string;
  repaymentFrequency?: string;
  loanCreatedDate?:string;
};

interface VehicleItem {
  vehicle_id: number;
  owner_id: number;
  vehicle_type?: string;
  manufacturer?: string;
  model?: string;
  variant?: string | null;
  registration_number?: string;
  chassis_number?: string;
  engine_number?: string;
  year_of_manufacture?: string | number | null;
  [key: string]: any;
}

interface CustomerItem {
  profile_id: number;
  username: string;
  mobile_number: string;
  vehicles?: VehicleItem[];
}

const loanTypes = ["New", "Used"];
const repaymentFrequencies = ["monthly"];

export default function CreateLoan({ onCreate }: { onCreate?: (payload: any) => void }) {
  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState<LoanPayload>({
    startDate: today,
    loanType: loanTypes[0],
    repaymentFrequency: repaymentFrequencies[0],
    loanCreatedDate: today, 
  });

  const [data, setData] = useState<CustomerItem[]>([]);
  const [vehicles, setVehicles] = useState<VehicleItem[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const navigate = useNavigate();
  // Loan Calculation States
  const [loanAmount, setLoanAmount] = useState<string>("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdLoanId, setCreatedLoanId] = useState<string | null>(null);
  const [tenure, setTenure] = useState<string>("");
  const [interest, setInterest] = useState<string>("");
    const [calculateAmount, setCalculateAmount] = useState<{
      totalPayable: number;
      monthlyDue: number;
      weeklyDue?: number;
      totalInterest?: number;
    } | null>(null);
    const [calcLoading, setCalcLoading] = useState(false);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [loancode, setLoancode] = useState<string>("");

    const handleChangeCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ""); // allow only digits
    setLoancode(value);
  };
  
    function computeEndDate(startDateStr?: string, monthsNum?: number, frequency?: string): string | null {
      if (!startDateStr || !monthsNum) return null;
      const d = new Date(startDateStr);
      if (isNaN(d.getTime())) return null;
      if (frequency === "weekly") {
        const WEEKS_PER_MONTH = 52 / 12; // ≈ 4.3333333
    const weeks = Math.round(monthsNum * WEEKS_PER_MONTH);
    d.setDate(d.getDate() + (weeks - 1) * 7);
      } else {
        // monthly
        d.setMonth(d.getMonth() + Math.round(monthsNum) - 1);
      }
        const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy}`;
    }

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res: any = await getProfiles();
        const customersRaw = (res.profiles || []).filter((p: any) => p.login?.role === "Customer");
        const customers: CustomerItem[] = customersRaw.map((p: any) => ({
          profile_id: p.profile_id,
          username: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.login?.username,
          mobile_number: p.login?.mobile_number ?? p.phone ?? "",
          vehicles: Array.isArray(p.vehicles)
            ? p.vehicles.map((v: any) => ({
              vehicle_id: v.vehicle_id,
              owner_id: v.owner_id,
              vehicle_type: v.vehicle_type,
              manufacturer: v.manufacturer,
              model: v.model,
              variant: v.variant,
              registration_number: v.registration_number,
              chassis_number: v.chassis_number,
              engine_number: v.engine_number,
              year_of_manufacture: v.year_of_manufacture,
              ...v,
            }))
            : [],
        }));
        setData(customers);
      } catch (err: any) {
        setError(err?.message ?? "Failed to load profiles");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  }

  function onCustomerSelected(val?: string, selected?: CustomerItem) {
    setForm((s) => ({
      ...s,
      customerId: val,
      customerMobile: selected?.mobile_number ?? s.customerMobile,
      vehicleId: undefined,
    }));

    const vs = selected?.vehicles ?? [];
    setVehicles(vs);
    setSelectedVehicle(null);
  }

  function selectVehicle(vehicleIdStr: string) {
    if (!vehicleIdStr) {
      setSelectedVehicle(null);
      setForm((s) => ({ ...s, vehicleId: undefined }));
      return;
    }
    const vid = Number(vehicleIdStr);
    const v = vehicles.find((x) => x.vehicle_id === vid) ?? null;
    setSelectedVehicle(v);
    setForm((s) => ({ ...s, vehicleId: String(vid) }));
  }

  const handleAddVehicle = () => {
    navigate("/customers/details", {
      state: { id: form.customerId, activeTab: "vehicle" },
    });
  };

     const handleLoanCalculate = async () => {
       const P = Number(loanAmount);
       const N = Number(tenure);
       const R = Number(interest);
   
       if (!P || !N || !R) {
         toast.error("Please enter loan amount, tenure, and interest rate");
         return;
       }
   
       setCalcLoading(true);
       try {
         const { data } = await axiosClient.post("/dashboard/loan-calculator", {
           loan_amount: P,
           interest_rate: R,
           tenure_months: N,
         });
   
         setCalculateAmount({
           totalPayable: data.total_amount ?? 0,
           monthlyDue: data.monthly_amount ?? 0,
           weeklyDue: data.weekly_amount ?? 0,
           totalInterest: data.total_interest ?? 0,
         });
   
         // Update form fields so submit has latest values
         setForm((prev) => ({
           ...prev,
           principal: String(P),
           interestRate: String(R),
           tenure: String(N),
         }));
  
        // compute and set end date based on selected frequency
        const end = computeEndDate(form.startDate ?? today, N, form.repaymentFrequency ?? repaymentFrequencies[0]);
        setEndDate(end);
       } catch (err: any) {
         console.error("Loan calculation failed:", err);
         toast.error("Loan calculation failed");
       } finally {
         setCalcLoading(false);
       }
     };

     const handleClearCalculation = () => {
       setLoanAmount("");
       setTenure("");
       setInterest("");
       setCalculateAmount(null);
     setForm((prev) => ({ ...prev, principal: undefined, interestRate: undefined, tenure: undefined }));
     setForm((prev) => ({ ...prev, principal: undefined, interestRate: undefined, tenure: undefined }));
     setEndDate(null);
     };

  const currency = (amount?: number) =>
    amount != null ? amount.toLocaleString("en-IN", { style: "currency", currency: "INR" }) : "₹0";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    
    if (!form.customerId) return
    //  toast.error("Please select a customer");
    if (!form.principal) return 
    // toast.error("Please calculate or enter principal amount.");

    setLoading(true);
    setError(null);
    try {
      const apiPayload = {
        borrower_id: form.customerId,
        vehicle_id: form.vehicleId ?? null,
        loan_type: form.loanType ?? "New",
        loan_amount: Number(form.principal),
        interest_rate: form.interestRate ? Number(form.interestRate) : undefined,
        tenure_months: form.tenure ? Number(form.tenure) : undefined,
        start_date: form.startDate,
        loan_created_date: form.loanCreatedDate,  
        repayment_frequency: form.repaymentFrequency ?? "monthly",
        documents: files.length ? files : undefined,
        loan_code: loancode ? `LN${loancode}` : undefined,
      };

      const res = await createLoan(apiPayload as any);

      if (!res.success) {
        setError(res.message ?? "Failed to create loan");
        toast.error(res.message ?? "Failed to create loan");
        return;
      }

      toast.success( "Loan created successfully!");
      setShowSuccessModal(true);
      
      if (onCreate) onCreate(apiPayload);
      // res.data is normalized; prefer loan_id but fallback to id
      const createdLoanId = res.data?.loan_id ?? res.data?.id ?? null;
     setCreatedLoanId(createdLoanId ? String(createdLoanId) : null);
    
      // Reset form
      setForm({ startDate: today, loanType: loanTypes[0], repaymentFrequency: repaymentFrequencies[0] });
      setFiles([]);
      setVehicles([]);
      setSelectedVehicle(null);
      handleClearCalculation();
      setEndDate(null);
    } catch (err: any) {
      setError(err?.message ?? "Failed to create loan");
      toast.error(err?.message ?? "Failed to create loan");
    } finally {
      setLoading(false);
    }
  }

     useEffect(() => {
      // recompute end date whenever startDate / tenure / frequency changes (useful when user toggles weekly/monthly)
      if (form.tenure) {
        const months = Number(form.tenure);
        const end = computeEndDate(form.startDate ?? today, months, form.repaymentFrequency ?? repaymentFrequencies[0]);
        setEndDate(end);
      } else {
        setEndDate(null);
      }
    }, [form.startDate, form.tenure, form.repaymentFrequency]);


   function handleModalClose() {
    setShowSuccessModal(false);
   if (createdLoanId) {
     navigate("/loan-dues", { state: { loan_id: createdLoanId } });
   } else {
     navigate(-1);
   }
   setCreatedLoanId(null);
   }

  return (
    <div className="pt-3  from-gray-50 to-gray-100 min-h-screen">
      <div className="mx-auto bg-white rounded-2xl shadow-lg p-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-800">Create New Loan</h2>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Customer Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Customer</label>
            {loading ? (
              <div className="text-sm text-gray-500">Loading customers…</div>
            ) : (
              <div>
                <AutocompleteCustomer
                  items={data}
                  value={form.customerId}
                  placeholder="Type name or mobile..."
                  onChange={onCustomerSelected}
                />
                {form.customerId && form.customerMobile && (
                  <div className="mt-2 text-xs text-gray-600 flex items-center gap-1">
                    <span>📞</span>
                    <span className="font-medium">{form.customerMobile}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Vehicles Section */}
          {form.customerId && (
            <>
              {vehicles.length > 0 ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Vehicle</label>
                  <select
                    name="vehicleId"
                    value={form.vehicleId || ""}
                    onChange={(e) => selectVehicle(e.target.value)}
                    className="w-full rounded-md border-gray-200 bg-white px-3 py-2 shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-300"
                  >
                    <option value="">Select a vehicle</option>
                    {vehicles.map((v) => (
                      <option key={v.vehicle_id} value={String(v.vehicle_id)}>
                        {v.manufacturer ?? v.vehicle_type} {v.model ?? ""} — {v.registration_number ?? v.chassis_number ?? ""}
                      </option>
                    ))}
                  </select>

                  {selectedVehicle && (
                    <div className="mt-3 bg-gray-50 border border-gray-200 rounded shadow p-3 grid grid-cols-2 gap-3 text-xs text-gray-600">
                      <div>
                        <div className="font-medium">{selectedVehicle.manufacturer} {selectedVehicle.model} ({selectedVehicle.variant ?? "—"})</div>
                        <div>{selectedVehicle.vehicle_type}</div>
                      </div>
                      <div>
                        <div>Reg: {selectedVehicle.registration_number ?? "—"}</div>
                        <div>Year: {selectedVehicle.year_of_manufacture ?? "—"}</div>
                        <div>Chassis: {selectedVehicle.chassis_number ?? "—"}</div>
                        <div>Engine: {selectedVehicle.engine_number ?? "—"}</div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded px-3 py-2">
                  <div className="text-sm">This customer has no vehicle. Add vehicle or choose another customer.</div>
                  <Button type="button" variant="secondary" onClick={handleAddVehicle}>
                    Add Vehicle
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Loan Calculation Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-white p-4  md:border-e  border-gray-200  space-y-3">
              <h3 className="text-lg font-semibold mb-2">Loan Calculation</h3>
              <InputField
                label="Loan Amount"
                type="number"
                value={loanAmount}
                onChange={(e) => setLoanAmount(e.target.value)}
                placeholder="Enter amount"
              />
              <InputField
                label="Tenure (months)"
                type="number"
                value={tenure}
                onChange={(e) => setTenure(e.target.value)}
                placeholder="Enter months"
              />
              <InputField
                label="Interest Rate (%)"
                type="number"
                value={interest}
                onChange={(e) => setInterest(e.target.value)}
                placeholder="Enter interest rate"
              />
              <div className="flex gap-2">
                <Button onClick={handleClearCalculation} fullWidth variant="secondary" disabled={calcLoading}>
                  Clear
                </Button>
                <Button onClick={handleLoanCalculate} fullWidth variant="primary" disabled={calcLoading}>
                  {calcLoading ? "Calculating..." : "Calculate"}
                </Button>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Loan Details</h3>
              {!calculateAmount ? (
                <div className="text-gray-500 italic">No loan calculation yet</div>
              ) : (
                <div className="space-y-3 text-gray-700">
                  <div className="flex justify-between text-md font-medium text-blue-400 rounded-md border-2 border-blue-200 bg-white px-3 py-2 shadow-sm">
                    <span>Total Payable:</span>
                    <span className="font-semibold">{currency(calculateAmount.totalPayable)}</span>
                  </div>
                  <div className="flex justify-between text-md font-medium text-green-400 rounded-md border-2 border-green-200 bg-white px-3 py-2 shadow-sm">
                    <span>Total Interest:</span>
                    <span className="font-semibold">{currency(calculateAmount.totalInterest)}</span>
                  </div>
                  <div className="flex justify-between text-md font-medium text-red-400 rounded-md border-2 border-red-200 bg-white px-3 py-2 shadow-sm">
                    <span>Monthly Due:</span>
                    <span className="font-semibold">{currency(calculateAmount.monthlyDue)}</span>
                  </div>
                  {/* <div className="flex justify-between text-md font-medium text-purple-400 rounded-md border-2 border-purple-200 bg-white px-3 py-2 shadow-sm">
                    <span>Weekly Due:</span>
                    <span className="font-semibold">{currency(calculateAmount.weeklyDue)}</span>
                  </div> */}
                </div>
              )}
            </div>
          </div>

          {/* Loan Type & Frequency */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <InputField
                type="date"
                name="startDate"
                value={form.startDate || today}
                onChange={handleChange}
                // className="w-full rounded-md border-gray-200 bg-white px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
              />
            </div>
            <div >
            
             {endDate && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                Loan End Date:
              </label>
                   <div className="w-full rounded-md border-gray-200 bg-white px-3 py-2 shadow-sm ">
                     {/* <span>Loan End Date:</span> */}
                     
                     <span className="font-semibold">{endDate}</span>
                   </div>
                   </div>
                 )}
            
            </div>
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type</label>
              <select
                name="loanType"
                value={form.loanType ?? loanTypes[0]}
                onChange={handleChange}
                className="w-full rounded-md border-gray-200 bg-white px-3 py-2 shadow-sm"
              >
                {loanTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div> */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Repayment Frequency</label>
              <select
                name="repaymentFrequency"
                value={form.repaymentFrequency ?? repaymentFrequencies[0]}
                onChange={handleChange}
                className="w-full rounded-md border-gray-200 bg-white px-3 py-2 shadow-sm"
              >
                {repaymentFrequencies.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
               <InputField
          label="Loan Code"
          type="number"
          value={loancode}
          required
          onChange={handleChangeCode}
          placeholder="eg: 0001"
          className="flex-1 ml-2 outline-none bg-transparent text-gray-800"
          // maxLength={6} 
          prefix="LN" 
        />
            </div>
            <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Loan Created Date
  </label>
  <InputField
    type="date"
    name="loanCreatedDate"
    value={form.loanCreatedDate || today}
    onChange={handleChange}
  />
</div>

          </div>

          {/* Actions */}
          <div className="pt-4 border-t border-gray-200 flex justify-end gap-4">
            <Button
              type="button"
              onClick={() => {
                setForm({ startDate: today, loanType: loanTypes[0], repaymentFrequency: repaymentFrequencies[0] });
                setFiles([]);
                setVehicles([]);
                setSelectedVehicle(null);
                handleClearCalculation();
              }}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? "Creating..." : "Create Loan"}
            </Button>
          </div>
        </form>
      </div>

       {/* Success Modal */}
      <Modal isOpen={showSuccessModal} onClose={handleModalClose} title="Loan Created Successful">
        <div className="flex flex-col items-center justify-center text-center space-y-4 py-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>

          <h3 className="text-lg font-medium text-gray-800">Loan has been created successfully!</h3>
          <div className="pt-2 flex justify-center gap-5 w-full">
            
            <Button onClick={handleModalClose} variant="primary">
              OK
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
